import usePxAuthContext from 'src/authentication/usePxAuthContext';
import GameSocketContextProvider from 'src/contexts/GameSocketContextProvider';
import Background from 'src/features/PixelStreaming/components/Background/Background';
import ContentViewer from 'src/features/PixelStreaming/components/ContentViewer/ContentViewer';
import HUD from 'src/features/PixelStreaming/components/HUD';
import PointsDisplay from 'src/features/PixelStreaming/components/PointsDisplay';
import PointsOfInterest from 'src/features/PixelStreaming/components/PointsOfInterest';
import SettingsOverlay from 'src/features/PixelStreaming/components/SettingsOverlay';
import { usePixelStreamingContext } from 'src/features/PixelStreaming/contexts/PixelStreamingContext';
import useDebugEmitUIInteract from 'src/features/PixelStreaming/hooks/useDebugEmitUIInteraction';
import useEmojisBinding from 'src/features/PixelStreaming/hooks/useEmojisBinding';
import useInitialPacket from 'src/features/PixelStreaming/hooks/useInitialPacket';
import useJoinSpace from 'src/features/PixelStreaming/hooks/useJoinSpace';
import useOpenExternalLink from 'src/features/PixelStreaming/hooks/useOpenExternalLink';
import useOpenModal from 'src/features/PixelStreaming/hooks/useOpenModal';
import useProfileUpdate from 'src/features/PixelStreaming/hooks/useProfileUpdate';
import useRefreshToken from 'src/features/PixelStreaming/hooks/useRefreshToken';
import useUserJoinAndExit from 'src/features/PixelStreaming/hooks/useUserJoinAndExit';
import ArcwareContextProvider from 'src/features/Arcware/components/ArcwareContextProvider';
import EmojiGestureSelection from 'src/features/NewVisitor/layouts/EmojiGestureSelection';
import UsageLimitReachedModal from 'src/features/Usage/components/UsageLimitReachedModal';
import UsageLimitReachedScreen from 'src/features/Usage/components/UsageLimitReachedScreen';
import UsageContextProvider, { useUsageContext } from 'src/features/Usage/contexts/UsageContextProvider';
import { IMetaverse3DPage } from 'src/interfaces/IPage';

import ImageSlotViewer from '../../../PixelStreaming/components/ImageSlot/ImageSlotViewer';
import UploadContentModalContainer from '../../components/UploadContentModal/UploadContentModalContainer';

type Props = {
  page: IMetaverse3DPage;
};

function ArcwareMetaversePageBody({ page }: Props) {
  const { metaverse, space } = page;
  const metaverseId = metaverse._id;
  const { hasUsage } = useUsageContext();
  const { isStreaming } = usePixelStreamingContext();
  const { user, authToken } = usePxAuthContext();

  useInitialPacket(metaverseId, undefined, authToken, user);
  useRefreshToken();
  useOpenExternalLink();
  useOpenModal();
  useJoinSpace(space._id);
  useProfileUpdate();
  useDebugEmitUIInteract();
  useUserJoinAndExit();
  useEmojisBinding(Boolean(metaverseId && authToken && user));

  return (
    <>
      {!isStreaming && space.background && <Background source={space.background} />}
      <UsageLimitReachedScreen isOwner={false} logo={metaverse?.logo} />
      {hasUsage && (
        <>
          <HUD />
          <EmojiGestureSelection />
          <PointsDisplay />
          <PointsOfInterest />
          <ContentViewer metaverse={metaverse} />
          <ImageSlotViewer metaverse={metaverse} />
          <SettingsOverlay />
        </>
      )}
      <UploadContentModalContainer metaverseId={metaverseId} isSiteAdmin={false} />
      <UsageLimitReachedModal isOwner={false} />
    </>
  );
}

export default function ArcwareMetaversePage({ page }: Props) {
  const { authToken } = usePxAuthContext();
  const { metaverse, space } = page;
  const { shareId } = space.environment.purewebConfiguration;

  return (
    <UsageContextProvider metaverseId={metaverse._id}>
      <GameSocketContextProvider metaverseId={metaverse._id} authToken={authToken}>
        <ArcwareContextProvider shareId={shareId ?? ''} logo={metaverse?.logo}>
          <ArcwareMetaversePageBody page={page} />
        </ArcwareContextProvider>
      </GameSocketContextProvider>
    </UsageContextProvider>
  );
}
